import { useMemo, useCallback } from 'react';
import { Extension } from '@codemirror/state';
import { keymap, EditorView } from '@codemirror/view';

export type DefinitionLocation = {
  path: string;
  line: number;
  column: number;
};

export type ImportInfo = {
  source: string; // The import path
  defaultImport?: string; // import X from ...
  namedImports: string[]; // import { A, B } from ...
  namespaceImport?: string; // import * as X from ...
};

// Parse import statements from code
export function parseImports(code: string): ImportInfo[] {
  const imports: ImportInfo[] = [];

  // Match import statements
  const importRegex = /import\s+(?:(?:(\w+)\s*,?\s*)?(?:\{\s*([^}]+)\s*\})?(?:\*\s+as\s+(\w+))?)\s+from\s+['"]([^'"]+)['"]/g;
  const defaultOnlyRegex = /import\s+(\w+)\s+from\s+['"]([^'"]+)['"]/g;

  let match;

  // Parse complex imports
  while ((match = importRegex.exec(code)) !== null) {
    const [, defaultImport, namedImportsStr, namespaceImport, source] = match;
    const namedImports = namedImportsStr
      ? namedImportsStr.split(',').map(s => {
          const parts = s.trim().split(/\s+as\s+/);
          return parts[parts.length - 1].trim();
        }).filter(Boolean)
      : [];

    imports.push({
      source,
      defaultImport: defaultImport || undefined,
      namedImports,
      namespaceImport: namespaceImport || undefined,
    });
  }

  // Parse default-only imports that might be missed
  while ((match = defaultOnlyRegex.exec(code)) !== null) {
    const [, defaultImport, source] = match;
    // Skip if already parsed
    if (!imports.some(i => i.source === source && i.defaultImport === defaultImport)) {
      imports.push({
        source,
        defaultImport,
        namedImports: [],
      });
    }
  }

  return imports;
}

// Find local definition in current file
export function findLocalDefinition(code: string, symbolName: string): { line: number; column: number } | null {
  const lines = code.split('\n');

  // Patterns to match definitions
  const patterns = [
    // Function declarations
    new RegExp(`^\\s*(?:export\\s+)?(?:async\\s+)?function\\s+${symbolName}\\s*[(<]`),
    // Arrow functions and const declarations
    new RegExp(`^\\s*(?:export\\s+)?(?:const|let|var)\\s+${symbolName}\\s*[=:]`),
    // Class declarations
    new RegExp(`^\\s*(?:export\\s+)?class\\s+${symbolName}\\s*[{<]`),
    // Interface declarations
    new RegExp(`^\\s*(?:export\\s+)?interface\\s+${symbolName}\\s*[{<]`),
    // Type declarations
    new RegExp(`^\\s*(?:export\\s+)?type\\s+${symbolName}\\s*[=<]`),
    // Enum declarations
    new RegExp(`^\\s*(?:export\\s+)?enum\\s+${symbolName}\\s*\\{`),
    // Method definitions in class
    new RegExp(`^\\s*(?:public|private|protected)?\\s*(?:static)?\\s*(?:async)?\\s*${symbolName}\\s*[(<]`),
  ];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    for (const pattern of patterns) {
      if (pattern.test(line)) {
        const column = line.indexOf(symbolName);
        return { line: i + 1, column: column + 1 };
      }
    }
  }

  return null;
}

// Get the word at cursor position
function getWordAtPosition(view: EditorView): { word: string; from: number; to: number } | null {
  const { state } = view;
  const pos = state.selection.main.head;
  const line = state.doc.lineAt(pos);
  const lineText = line.text;
  const linePos = pos - line.from;

  // Find word boundaries
  let start = linePos;
  let end = linePos;

  // Move backward to find start of word
  while (start > 0 && /[\w$]/.test(lineText[start - 1])) {
    start--;
  }

  // Move forward to find end of word
  while (end < lineText.length && /[\w$]/.test(lineText[end])) {
    end++;
  }

  if (start === end) return null;

  const word = lineText.slice(start, end);
  return { word, from: line.from + start, to: line.from + end };
}

export type PeekDefinitionData = {
  symbolName: string;
  filePath: string;
  code: string;
  line: number;
  column: number;
  position: { x: number; y: number };
};

type UseGoToDefinitionParams = {
  workingFiles: Record<string, string>;
  treeIndex: {
    byPath: Record<string, { id: string; path: string }>;
  };
  resolvePath: (id: string) => string;
  onNavigate: (path: string, line: number, column: number) => void;
  onPeekDefinition?: (data: PeekDefinitionData) => void;
};

export function useGoToDefinition({
  workingFiles,
  treeIndex,
  resolvePath,
  onNavigate,
  onPeekDefinition,
}: UseGoToDefinitionParams) {

  // Resolve import path to actual file path
  const resolveImportPath = useCallback((currentPath: string, importSource: string): string | null => {
    // Handle relative imports
    if (importSource.startsWith('.')) {
      const currentDir = currentPath.split('/').slice(0, -1).join('/');
      let resolvedPath = importSource;

      if (importSource.startsWith('./')) {
        resolvedPath = `${currentDir}/${importSource.slice(2)}`;
      } else if (importSource.startsWith('../')) {
        const parts = currentDir.split('/');
        let upCount = 0;
        let remaining = importSource;
        while (remaining.startsWith('../')) {
          upCount++;
          remaining = remaining.slice(3);
        }
        resolvedPath = [...parts.slice(0, -upCount), remaining].join('/');
      }

      // Try different extensions
      const extensions = ['', '.ts', '.tsx', '.js', '.jsx', '/index.ts', '/index.tsx', '/index.js', '/index.jsx'];
      for (const ext of extensions) {
        const fullPath = resolvedPath + ext;
        if (treeIndex.byPath[fullPath]) {
          return fullPath;
        }
      }
    }

    // Handle absolute imports (e.g., @/components/...)
    if (importSource.startsWith('@/')) {
      const relativePath = 'src/' + importSource.slice(2);
      const extensions = ['', '.ts', '.tsx', '.js', '.jsx', '/index.ts', '/index.tsx'];
      for (const ext of extensions) {
        const fullPath = relativePath + ext;
        if (treeIndex.byPath[fullPath]) {
          return fullPath;
        }
      }
    }

    return null;
  }, [treeIndex]);

  // Find definition for a symbol
  const findDefinition = useCallback((view: EditorView): boolean => {
    const wordInfo = getWordAtPosition(view);
    if (!wordInfo) return false;

    const { word } = wordInfo;
    const code = view.state.doc.toString();

    // First check for local definition
    const localDef = findLocalDefinition(code, word);
    if (localDef) {
      // Navigate to local definition
      const line = view.state.doc.line(localDef.line);
      view.dispatch({
        selection: { anchor: line.from + localDef.column - 1 },
        scrollIntoView: true,
      });
      return true;
    }

    // Check if it's an imported symbol
    const imports = parseImports(code);
    for (const imp of imports) {
      const isImported =
        imp.defaultImport === word ||
        imp.namedImports.includes(word) ||
        imp.namespaceImport === word;

      if (isImported) {
        // Get current file path from the tree
        // We need to find the current file path - this is passed from the editor context
        const currentPath = Object.keys(workingFiles).find(path =>
          workingFiles[path] === code
        );

        if (currentPath) {
          const targetPath = resolveImportPath(currentPath, imp.source);
          if (targetPath) {
            const targetCode = workingFiles[targetPath];
            if (targetCode) {
              // Find the symbol definition in the target file
              const targetDef = findLocalDefinition(targetCode, word);
              if (targetDef) {
                onNavigate(targetPath, targetDef.line, targetDef.column);
                return true;
              }
              // If not found, just go to the file
              onNavigate(targetPath, 1, 1);
              return true;
            } else {
              // File not loaded yet, just navigate to it
              onNavigate(targetPath, 1, 1);
              return true;
            }
          }
        }
      }
    }

    return false;
  }, [workingFiles, resolveImportPath, onNavigate]);

  // Peek definition (show inline without navigating)
  const peekDefinition = useCallback((view: EditorView): boolean => {
    if (!onPeekDefinition) return false;

    const wordInfo = getWordAtPosition(view);
    if (!wordInfo) return false;

    const { word } = wordInfo;
    const code = view.state.doc.toString();

    // Get cursor position for popup placement
    const coords = view.coordsAtPos(wordInfo.from);
    const position = coords ? { x: coords.left, y: coords.bottom + 5 } : { x: 100, y: 100 };

    // First check for local definition
    const localDef = findLocalDefinition(code, word);
    if (localDef) {
      // Get current file path
      const currentPath = Object.keys(workingFiles).find(path =>
        workingFiles[path] === code
      ) || 'current-file';

      onPeekDefinition({
        symbolName: word,
        filePath: currentPath,
        code,
        line: localDef.line,
        column: localDef.column,
        position,
      });
      return true;
    }

    // Check if it's an imported symbol
    const imports = parseImports(code);
    for (const imp of imports) {
      const isImported =
        imp.defaultImport === word ||
        imp.namedImports.includes(word) ||
        imp.namespaceImport === word;

      if (isImported) {
        const currentPath = Object.keys(workingFiles).find(path =>
          workingFiles[path] === code
        );

        if (currentPath) {
          const targetPath = resolveImportPath(currentPath, imp.source);
          if (targetPath) {
            const targetCode = workingFiles[targetPath];
            if (targetCode) {
              const targetDef = findLocalDefinition(targetCode, word);
              if (targetDef) {
                onPeekDefinition({
                  symbolName: word,
                  filePath: targetPath,
                  code: targetCode,
                  line: targetDef.line,
                  column: targetDef.column,
                  position,
                });
                return true;
              }
              // If symbol not found but file exists, show start of file
              onPeekDefinition({
                symbolName: word,
                filePath: targetPath,
                code: targetCode,
                line: 1,
                column: 1,
                position,
              });
              return true;
            }
          }
        }
      }
    }

    return false;
  }, [workingFiles, resolveImportPath, onPeekDefinition]);

  // Create extension with F12, Alt+F12, and Cmd+Click bindings
  const goToDefinitionExtension = useMemo<Extension>(() => [
    keymap.of([
      {
        key: 'F12',
        run: (view) => findDefinition(view),
      },
      {
        key: 'Alt-F12',
        run: (view) => peekDefinition(view),
      },
    ]),
    // Cmd+Click handler
    EditorView.domEventHandlers({
      click: (event, view) => {
        if (event.metaKey || event.ctrlKey) {
          event.preventDefault();
          // Position cursor at click location first
          const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
          if (pos !== null) {
            view.dispatch({
              selection: { anchor: pos },
            });
            // Then find definition
            setTimeout(() => findDefinition(view), 0);
          }
          return true;
        }
        return false;
      },
    }),
  ], [findDefinition, peekDefinition]);

  return { goToDefinitionExtension, findDefinition, peekDefinition };
}
